import * as React from "react";
import {
    Card,
    CardContent,
    Chip,
    FormControl,
    MenuItem,
    Radio,
    Select,
    Skeleton,
    Stack,
    Typography,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import {alpha} from "@mui/material/styles";
import {useTranslation} from "react-i18next";
import type {AllPayrollPeriod} from "../../../services/payrollService";
import {parsePeriodKey, formatPeriodLabel} from "../../../utils/periodLabel";
import {formatShortDateRange} from "./periodFormat";

interface PayrollPeriodListPanelProps {
    allPeriods: AllPayrollPeriod[];
    selectedYear: number;
    years: number[];
    handleYearChange: (event: any) => void;
    selectedPeriod: string | null;
    onSelectPeriod: (periodKey: string, value: string) => void;
    loading?: boolean;
}

export default function PayrollPeriodListPanel({
    allPeriods,
    selectedYear,
    years,
    handleYearChange,
    selectedPeriod,
    onSelectPeriod,
    loading,
}: Readonly<PayrollPeriodListPanelProps>): React.ReactElement {
    const {t} = useTranslation();

    const periodsForYear = React.useMemo(() => {
        return allPeriods
            .map((p) => ({period: p, parsed: parsePeriodKey(p.periodKey)}))
            .filter((p) => p.parsed?.year === selectedYear)
            .sort((a, b) => (b.parsed?.month ?? 0) - (a.parsed?.month ?? 0))
            .map((p) => p.period);
    }, [allPeriods, selectedYear]);

    return (
        <Card variant="outlined" sx={{borderRadius: 1, height: "100%"}}>
            <CardContent>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{mb: 2}}>
                    <Typography variant="subtitle1" fontWeight={700}>
                        {t('payroll.period.panel.title')}
                    </Typography>
                    <FormControl size="small" sx={{minWidth: 100}}>
                        <Select value={selectedYear} onChange={handleYearChange}>
                            {years.map((year) => (
                                <MenuItem key={year} value={year}>{year}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Stack>

                {loading ? (
                    <Stack spacing={1.5}>
                        {Array.from({length: 3}).map((_, i) => (
                            <Skeleton key={i} variant="rectangular" height={70} sx={{borderRadius: 1}}/>
                        ))}
                    </Stack>
                ) : periodsForYear.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{py: 2}}>
                        {t('payroll.period.table.noPeriodsFound', {month: '', year: selectedYear})}
                    </Typography>
                ) : (
                    <Stack spacing={1.5}>
                        {periodsForYear.map(({periodKey, value, status}) => {
                            const isSelected = periodKey === selectedPeriod;
                            const isLocked = status?.toUpperCase().includes('LOCKED') ?? false;
                            return (
                                <Card
                                    key={periodKey}
                                    variant="outlined"
                                    onClick={() => onSelectPeriod(periodKey, value)}
                                    sx={{
                                        borderRadius: 1,
                                        cursor: "pointer",
                                        borderColor: isSelected ? "success.main" : "divider",
                                        backgroundColor: isSelected
                                            ? (theme) => alpha(theme.palette.success.main, 0.08)
                                            : "transparent",
                                        transition: "all 0.15s ease",
                                        "&:hover": {boxShadow: 1},
                                    }}
                                >
                                    <CardContent sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        gap: 1,
                                        "&:last-child": {pb: 2},
                                    }}>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <Radio checked={isSelected} size="small" sx={{p: 0}}/>
                                            <Stack sx={{textAlign: "left"}}>
                                                <Typography variant="body1" fontWeight={600}>
                                                    {formatPeriodLabel(t, periodKey, value)}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {formatShortDateRange(periodKey)}
                                                </Typography>
                                            </Stack>
                                        </Stack>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            {isLocked && <LockOutlinedIcon fontSize="small" sx={{color: "text.secondary"}}/>}
                                            <Chip
                                                label={isLocked ? t('dashboard.wizard.status.completed') : t('dashboard.wizard.status.inProgress')}
                                                size="small"
                                                color={isLocked ? "success" : "warning"}
                                                variant="outlined"
                                            />
                                        </Stack>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </Stack>
                )}
            </CardContent>
        </Card>
    );
}
